import React, { useEffect, useState } from 'react';
import { Card, Space, Tag, Typography, Button, Spin } from 'antd';
import { AppleOutlined, LinkOutlined, QrcodeOutlined } from '@ant-design/icons';
import { AppStoreStatus, queryAppStoreStatus } from '@/api/version';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';

const { Text, Link } = Typography;

interface AppStoreInfoProps {
  appUrl: string;
}

const STATUS_MAP = {
  PREPARE_FOR_SUBMISSION: { text: '准备提交', color: 'default' },
  WAITING_FOR_REVIEW: { text: '等待审核', color: 'processing' },
  IN_REVIEW: { text: '审核中', color: 'processing' },
  PENDING_DEVELOPER_RELEASE: { text: '待发布', color: 'warning' },
  READY_FOR_SALE: { text: '已上线', color: 'success' },
  REJECTED: { text: '被拒绝', color: 'error' },
};

const AppStoreInfo: React.FC<AppStoreInfoProps> = ({ appUrl }) => {
  const [loading, setLoading] = useState(false);
  const [storeStatus, setStoreStatus] = useState<AppStoreStatus>();

  const fetchStoreStatus = async () => {
    try {
      setLoading(true);
      const resp = await queryAppStoreStatus();
      if (resp.status === 200 && resp.data) {
        setStoreStatus(resp.data);
      }
    } catch (error) {
      console.error('获取 App Store 状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoreStatus();
    // 每 5 分钟刷新一次状态
    const timer = setInterval(fetchStoreStatus, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const handleOpenAppStore = () => {
    window.open(appUrl, '_blank');
  };

  const status = storeStatus?.status
    ? STATUS_MAP[storeStatus.status]
    : undefined;

  return (
    <Card
      size="small"
      title={
        <Space>
          <AppleOutlined />
          <span>App Store 信息(功能完善中...)</span>
        </Space>
      }
      extra={
        <Space>
          <Button
            type="link"
            icon={<QrcodeOutlined />}
            onClick={() => window.open(`https://qr.ae/${appUrl}`, '_blank')}
          >
            二维码
          </Button>
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={handleOpenAppStore}
          >
            查看详情
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Space direction="vertical" size="small">
          <Space>
            <Text>App ID:</Text>
            <Text type="secondary">6739333297</Text>
          </Space>
          <Space>
            <Text>状态:</Text>
            {status ? (
              <Tag color={status.color}>{status.text}</Tag>
            ) : (
              <Tag>未知</Tag>
            )}
          </Space>
          {storeStatus?.version && (
            <Space>
              <Text>版本:</Text>
              <Text type="secondary">{storeStatus.version}</Text>
            </Space>
          )}
          {storeStatus?.updateTime && (
            <Space>
              <Text>更新时间:</Text>
              <Text type="secondary">
                {dayjs(storeStatus.updateTime).format(FORMAT_TIME)}
              </Text>
            </Space>
          )}
          {storeStatus?.description && (
            <Space>
              <Text>备注:</Text>
              <Text type="secondary">{storeStatus.description}</Text>
            </Space>
          )}
          <Space>
            <Text>链接:</Text>
            <Link href={appUrl} target="_blank">
              App Store
            </Link>
          </Space>
        </Space>
      </Spin>
    </Card>
  );
};

export default AppStoreInfo;
